// Bounded, tenant/user-bound offline mutation queue with idempotency keys, ordered replay,
// conflict detection, retry ceiling, expiry, size limits and honest states. ONLY an explicit
// low-risk allowlist may queue; high-risk actions (clock punches, payroll, permissions, billing,
// identity, audit, schedule publish, emergency overrides) are never eligible.
const KEY = "sc_offline_queue_v1";
const MAX_ITEMS = 50;
const TTL_MS = 24 * 60 * 60 * 1000; // 24h
const RETRY_MAX = 5;

// Allowlist of {method, pathPrefix}. Freshness item logging is idempotent server-side via client_id.
const ALLOWLIST: { method: string; prefix: string }[] = [
  { method: "POST", prefix: "/freshness/items" },
];

export type QState = "queued" | "failed" | "conflict";
export interface QItem {
  id: string; method: string; url: string; body: any;
  tenantId: string; userId: string; createdAt: number; expiresAt: number;
  retries: number; state: QState; lastError?: string;
}

function uuid(): string {
  return (crypto as any).randomUUID ? (crypto as any).randomUUID()
    : "id-" + Date.now() + "-" + Math.random().toString(16).slice(2);
}
function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}
function readAll(): QItem[] {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
}
function writeAll(items: QItem[]) { localStorage.setItem(KEY, JSON.stringify(items)); }

export function isAllowlisted(method: string, path: string): boolean {
  return ALLOWLIST.some((a) => a.method === method.toUpperCase() && path.startsWith(a.prefix));
}

function prune(items: QItem[]): QItem[] {
  const now = Date.now();
  return items.filter((i) => i.expiresAt > now);
}

export function listQueue(userId?: string): QItem[] {
  const items = prune(readAll());
  return userId ? items.filter((i) => i.userId === userId) : items;
}
export function queueCounts(userId: string) {
  const items = listQueue(userId);
  return {
    queued: items.filter((i) => i.state === "queued").length,
    failed: items.filter((i) => i.state === "failed").length,
    conflict: items.filter((i) => i.state === "conflict").length,
    total: items.length,
  };
}

// Enqueue a low-risk mutation. Returns the idempotency id. Throws if not allowlisted or full.
export function enqueue(path: string, method: string, body: any, tenantId: string, userId: string): string {
  if (!isAllowlisted(method, path)) throw new Error("This action cannot be queued offline");
  let items = prune(readAll());
  if (items.length >= MAX_ITEMS) throw new Error("Offline queue is full — reconnect to sync");
  const id = uuid();
  const now = Date.now();
  items.push({ id, method: method.toUpperCase(), url: path,
    body: { ...body, client_id: id }, // idempotency key doubles as server client_id
    tenantId, userId, createdAt: now, expiresAt: now + TTL_MS, retries: 0, state: "queued" });
  writeAll(items);
  return id;
}

export function clearAll() { localStorage.removeItem(KEY); }
export function clearForUser(userId: string) {
  writeAll(readAll().filter((i) => i.userId !== userId));
}

// Ordered replay of the current user's queued items. Stops on the first network failure (offline).
// Returns a summary. Idempotency guarantees no duplicate server mutation on replay.
export async function flush(apiBase: string, tenantId: string, userId: string): Promise<{ applied: number; conflicts: number; failed: number; remaining: number }> {  if (!navigator.onLine) return { applied: 0, conflicts: 0, failed: 0, remaining: listQueue(userId).length };
  let items = prune(readAll());
  const mine = items.filter((i) => i.userId === userId && i.tenantId === tenantId && i.state === "queued")
    .sort((a, b) => a.createdAt - b.createdAt);
  let applied = 0, conflicts = 0, failed = 0;
  const csrf = getCookie("sc_csrf");
  for (const item of mine) {
    let res: Response;
    try {
      res = await fetch(apiBase + item.url, {
        method: item.method, credentials: "include",
        headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          "X-Idempotency-Key": item.id },
        body: JSON.stringify(item.body),
      });
    } catch (e) {
      // network dropped mid-flush -> keep item queued, stop (still offline)
      break;
    }
    items = prune(readAll());
    const idx = items.findIndex((i) => i.id === item.id);
    if (idx < 0) continue;
    if (res.ok) {
      items.splice(idx, 1); applied++;               // applied (idempotent replay is safe)
    } else if (res.status === 409) {
      items[idx].state = "conflict"; items[idx].lastError = "conflict"; conflicts++;
    } else if (res.status >= 400 && res.status < 500) {
      items[idx].retries += 1;
      items[idx].state = items[idx].retries >= RETRY_MAX ? "failed" : "queued";
      items[idx].lastError = `http_${res.status}`;
      if (items[idx].state === "failed") failed++;
    } else {
      items[idx].retries += 1;
      if (items[idx].retries >= RETRY_MAX) { items[idx].state = "failed"; failed++; }
    }
    writeAll(items);
  }
  return { applied, conflicts, failed, remaining: listQueue(userId).filter((i) => i.state === "queued").length };
}

// E2E/diagnostic affordance: exposes READ-ONLY queue introspection (no mutation helpers).
if (typeof window !== "undefined") {
  (window as any).__scOfflineQueue = { listQueue, queueCounts, isAllowlisted };
}
